import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Clock, Building2, Award, Store, Users, CreditCard, UserCheck, Puzzle } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useAlert } from '../../../context/AlertContext';
import { useSettingsData } from './hooks/useSettingsData';

// Alt Bileşen İçe Aktarımları
import CompanyProfile from './components/CompanyProfile';
import LoyaltyTiers from './components/LoyaltyTiers';
import VisitRoles from './components/VisitRoles';
import GeneralRules from './components/GeneralRules';
import MasterQRCode from './components/MasterQRCode';
import SubscriptionPayments from './components/SubscriptionPayments';
import Integrations from './components/Integrations';
import MenuOrderingSetting from './components/MenuOrderingSetting';

// Team Management Imports (Migrated from UsersAndRoles)
import UserTable from '../../admin/users/UserTable';
import RoleGrid from '../../admin/users/RoleGrid';
import RoleModal from '../../admin/users/RoleModal';
import InviteUserModal from '../../admin/users/InviteUserModal';
import { roleService } from '../../../services/roleService';
import { supabase } from '../../../supabase/config';
import { ShieldCheck, UserPlus, RefreshCw } from 'lucide-react';
const Settings = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { tenantData, effectiveTenantId, user, userData, hasPermission, permissionsLoading } = useAuth();
    const { showAlert, showConfirm } = useAlert();

    // Active tab based on URL
    const [activeTab, setActiveTab] = React.useState('profile');

    React.useEffect(() => {
        if (location.pathname.endsWith('/qr')) {
            setActiveTab('qr');
        } else if (location.pathname.endsWith('/loyalty')) {
            setActiveTab('loyalty');
        } else if (location.pathname.endsWith('/visits')) {
            setActiveTab('visits');
        } else if (location.pathname.endsWith('/users')) {
            setActiveTab('users');
        } else if (location.pathname.endsWith('/roles')) {
            setActiveTab('roles');
        } else if (location.pathname.endsWith('/integrations')) {
            setActiveTab('integrations');
        } else if (location.pathname.endsWith('/payments')) {
            setActiveTab('payments');
        } else {
            setActiveTab('profile');
        }
    }, [location.pathname]);

    const handleTabClick = (id) => {
        if (id === 'profile') navigate('/app/settings');
        else navigate(`/app/settings/${id}`);
    };

    const {
        uploading, bannerUploading, handleLogoUpload, handleBannerUpload, handleUpdateField,
        loyaltyDraft, setLoyaltyDraft, handleSaveLoyalty, isSavingLoyalty,
        rolesDraft, setRolesDraft, handleSaveRoles, isSavingRoles,
        profileDraft, setProfileDraft, handleSaveProfile, isSavingProfile,
        generalDraft, setGeneralDraft, handleSaveGeneral, isSavingGeneral,
        generateApiKey, deleteApiKey
    } = useSettingsData(tenantData, effectiveTenantId, userData, user);

    // --- MIGRATED USERS & ROLES STATE ---
    const canManageRoles = userData?.role === 'owner' || userData?.isSuperAdmin || hasPermission('roles', 'manage');
    const [users, setUsers] = React.useState([]);
    const [roles, setRoles] = React.useState([]);
    const [tenants, setTenants] = React.useState({});
    const [loading, setLoading] = React.useState(true);
    const [modalOpen, setModalOpen] = React.useState(null); // 'user' or 'role'
    const [editingRole, setEditingRole] = React.useState(null);

    const initialRole = {
        name: '',
        permissions: {
            customers: { view: true, create: false, edit: false, delete: false },
            discounts: { view: true, create: false, edit: false, delete: false },
            rewards: { view: true, create: false, edit: false, delete: false },
            menu: { view: true, create: false, edit: false, delete: false },
            orders: { view: true, create: false, edit: false, delete: false },
            inventory: { view: true, edit: false },
            accounting: { view: false },
            analytics: { view: false },
            notifications: { view: true },
            support: { view: true, create: true },
            activities: { view: true },
            settings: { view: false, edit: false },
            roles: { view: false, manage: false }
        }
    };
    const [roleData, setRoleData] = React.useState(initialRole);

    React.useEffect(() => {
        if (!effectiveTenantId && userData?.isSuperAdmin) {
            supabase.from('tenants').select('id, name').then(({ data }) => {
                const map = {};
                (data || []).forEach(d => map[d.id] = d.name);
                setTenants(map);
            });
        }
    }, [effectiveTenantId, userData?.isSuperAdmin]);

    React.useEffect(() => {
        let unsubs = [];
        setLoading(true);
        unsubs.push(roleService.subscribeToUsers(effectiveTenantId, userData?.isSuperAdmin, setUsers));
        if (effectiveTenantId) {
            unsubs.push(roleService.subscribeToRoles(effectiveTenantId, (data) => {
                setRoles(data);
                setLoading(false);
            }));
        } else { setLoading(false); }
        return () => unsubs.forEach(u => u && u());
    }, [effectiveTenantId, userData?.isSuperAdmin]);

    const handleSaveRole = async (e) => {
        e.preventDefault();
        try {
            await roleService.saveRole(effectiveTenantId, roleData, editingRole?.id, user.uid, userData.email);
            setModalOpen(null);
        } catch (err) { showAlert("Hata oluştu.", "error"); }
    };

    const handleDeleteRole = async (role) => {
        if (await showConfirm(`${role.name} rolünü silmek istediğinize emin misiniz?`, { title: 'Rolü Sil', confirmText: 'Sil', type: 'warning' })) {
            await roleService.deleteRole(effectiveTenantId, role.id, role.name, user.uid, userData.email);
        }
    };
    
    const handleSeedDefaultRoles = async () => {
        if (!await showConfirm("Varsayılan rolleri (Garson, Kasiyer, Mutfak) oluşturmak istediğinize emin misiniz?", { title: 'Varsayılan Roller', confirmText: 'Oluştur', type: 'warning' })) return;
        setLoading(true);
        try {
            await roleService.seedDefaultRoles(effectiveTenantId, user.uid, userData.email);
        } catch (err) {
            showAlert("Hata oluştu: " + err.message, "error");
        } finally {
            setLoading(false);
        }
    };
    if (permissionsLoading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
                <div className="animate-spin" style={{ width: '32px', height: '32px', border: '4px solid var(--primary)', borderTopColor: 'transparent', borderRadius: '50%' }}></div>
            </div>
        );
    }

    if (!userData?.isSuperAdmin && !hasPermission('settings', 'view')) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
                <Clock size={48} style={{ color: 'var(--text-muted)', marginBottom: '1rem', opacity: 0.5 }} />
                <h3>{t('common.accessDenied')}</h3>
                <p style={{ color: 'var(--text-muted)' }}>{t('common.noPermission')}</p>
            </div>
        );
    }

    const canEdit = hasPermission('settings', 'edit');

    const plan = tenantData?.plan || 'free';
    const hasPaid = plan === 'pro' || plan === 'premium';
    const isPremium = plan === 'premium';

    const allTabs = [
        { id: 'profile', label: 'İşletme Profili', icon: <Building2 size={18} /> },
        { id: 'qr', label: 'QR Yönetimi', icon: <Store size={18} />, minPlan: 'pro' },
        { id: 'loyalty', label: 'Sadakat Ayarları', icon: <Award size={18} />, minPlan: 'pro' },
        { id: 'visits', label: 'Ziyaret Rolleri', icon: <UserCheck size={18} />, minPlan: 'pro' },
        { id: 'users', label: t('roles.usersTab'), icon: <Users size={18} />, minPlan: 'pro' },
        { id: 'roles', label: t('roles.rolesTab'), icon: <ShieldCheck size={18} />, minPlan: 'pro' },
        { id: 'integrations', label: 'Entegrasyonlar', icon: <Puzzle size={18} />, minPlan: 'premium' },
        { id: 'payments', label: 'Abonelik & Ödeme', icon: <CreditCard size={18} /> }
    ];

    const tabs = allTabs.filter(tab => {
        if (!tab.minPlan) return true;
        if (tab.minPlan === 'pro') return hasPaid;
        if (tab.minPlan === 'premium') return isPremium;
        return true;
    });

    return (
        <div style={{ maxWidth: '100%', overflowX: 'hidden' }}>
            <header style={{ marginBottom: '2.5rem' }}>
                <div style={{ marginBottom: '1.5rem' }}>
                    <h1 style={{ margin: '0 0 0.5rem 0', fontWeight: 900, fontSize: '2.25rem', letterSpacing: '-0.02em' }}>İşletme Ayarları</h1>
                    <p style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Sistem ayarlarınızı, sadakat programınızı ve işletme profilinizi buradan yönetin.</p>
                </div>
            </header>

            {/* SYNC: Tab Navigation - Exactly matching Promotions dashboard spacing */}
            <div style={{ marginBottom: '0' }}>
                <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    background: 'rgba(var(--primary-rgb), 0.05)',
                    padding: '0.5rem',
                    borderRadius: '16px',
                    width: 'fit-content',
                    maxWidth: '100%',
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'none'
                }} className="settings-tabs-nav hide-scrollbar">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => handleTabClick(tab.id)}
                            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '10px 24px',
                                borderRadius: '12px',
                                border: 'none',
                                background: activeTab === tab.id ? 'var(--primary)' : 'transparent',
                                color: activeTab === tab.id ? 'var(--text-on-primary)' : 'var(--text-main)',
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <span className="tab-icon" style={{ display: 'flex', alignItems: 'center' }}>
                                {tab.icon && React.cloneElement(tab.icon, { size: 18 })}
                            </span>
                            <span className="tab-label">
                                {tab.id === 'profile' ? (
                                    <>
                                        <span className="full-text">{tab.label}</span>
                                        <span className="short-text">Profil</span>
                                    </>
                                ) : tab.id === 'loyalty' ? (
                                    <>
                                        <span className="full-text">{tab.label}</span>
                                        <span className="short-text">Sadakat</span>
                                    </>
                                ) : tab.id === 'visits' ? (
                                    <>
                                        <span className="full-text">{tab.label}</span>
                                        <span className="short-text">Ziyaret</span>
                                    </>
                                ) : tab.id === 'qr' ? (
                                    <>
                                        <span className="full-text">{tab.label}</span>
                                        <span className="short-text">QR Kod</span>
                                    </>
                                ) : tab.id === 'users' ? (
                                    <>
                                        <span className="full-text">{tab.label}</span>
                                        <span className="short-text">Ekip</span>
                                    </>
                                ) : tab.id === 'roles' ? (
                                    <>
                                        <span className="full-text">{tab.label}</span>
                                        <span className="short-text">Yetki</span>
                                    </>
                                ) : tab.id === 'integrations' ? (
                                    <>
                                        <span className="full-text">{tab.label}</span>
                                        <span className="short-text">Entgr.</span>
                                    </>
                                ) : tab.id === 'payments' ? (
                                    <>
                                        <span className="full-text">{tab.label}</span>
                                        <span className="short-text">Kasa</span>
                                    </>
                                ) : tab.label}
                            </span>
                        </button>
                    ))}
                </div>
                <style>{`
                    .hide-scrollbar::-webkit-scrollbar { display: none; }
                    .settings-tabs-nav .short-text { display: none; }

                    @media (max-width: 600px) {
                        .settings-tabs-nav { width: 100% !important; padding: 0.3rem !important; gap: 0.5rem !important; overflow-x: auto !important; }
                        .settings-tabs-nav .tab-btn {
                            flex: 0 0 auto !important;
                            padding: 8px 16px !important;
                            font-size: 0.8rem !important;
                        }
                    }
                `}</style>
            </div>

            <div style={{ minHeight: '60vh', marginTop: '1rem' }}>
                {/* 1. İşletme Profili Tab */}
                {activeTab === 'profile' && profileDraft && (
                    <CompanyProfile
                        t={t}
                        tenantData={tenantData}
                        canEdit={canEdit}
                        profileDraft={profileDraft}
                        setProfileDraft={setProfileDraft}
                        handleSaveProfile={handleSaveProfile}
                        isSavingProfile={isSavingProfile}
                        handleLogoUpload={handleLogoUpload}
                        handleBannerUpload={handleBannerUpload}
                        uploading={uploading}
                        bannerUploading={bannerUploading}
                    />
                )}

                {/* 2. QR Yönetimi Tab */}
                {activeTab === 'qr' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                        <MasterQRCode
                            t={t}
                            tenantData={tenantData}
                            effectiveTenantId={effectiveTenantId}
                            handleUpdateField={handleUpdateField}
                            canEdit={canEdit}
                        />
                        <MenuOrderingSetting
                            tenantData={tenantData}
                            handleUpdateField={handleUpdateField}
                            canEdit={canEdit}
                        />
                    </div>
                )}

                {/* 3. Sadakat Ayarları Tab */}
                {activeTab === 'loyalty' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                        <GeneralRules
                            t={t}
                            tenantData={tenantData}
                            generalDraft={generalDraft}
                            setGeneralDraft={setGeneralDraft}
                            handleSaveGeneral={handleSaveGeneral}
                            isSavingGeneral={isSavingGeneral}
                        />
                        <LoyaltyTiers
                            t={t}
                            canEdit={canEdit}
                            loyaltyDraft={loyaltyDraft}
                            setLoyaltyDraft={setLoyaltyDraft}
                            tenantData={tenantData}
                            handleSaveLoyalty={handleSaveLoyalty}
                            isSavingLoyalty={isSavingLoyalty}
                        />
                    </div>
                )}

                {/* 4. Ziyaret Rolleri Tab */}
                {activeTab === 'visits' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                        <VisitRoles
                            t={t}
                            canEdit={canEdit}
                            rolesDraft={rolesDraft}
                            setRolesDraft={setRolesDraft}
                            tenantData={tenantData}
                            handleSaveRoles={handleSaveRoles}
                            isSavingRoles={isSavingRoles}
                        />
                    </div>
                )}

                {/* 5. Kullanıcılar Tab */}
                {activeTab === 'users' && (
                    <section className="fade-in">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                            <h2 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('roles.usersTab')}</h2>
                            {canManageRoles && <button onClick={() => setModalOpen('user')} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '12px', fontWeight: 700 }}><UserPlus size={18} /> {t('roles.addUser')}</button>}
                        </div>
                        {loading ? <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100px' }}><RefreshCw className="animate-spin" /></div> : (
                            <UserTable users={users} tenants={tenants} isSuperAdmin={userData?.isSuperAdmin} effectiveTenantId={effectiveTenantId} canManageRoles={canManageRoles} t={t} />
                        )}
                    </section>
                )}

                {/* 6. Roller Tab */}
                {activeTab === 'roles' && (
                    <section className="fade-in">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                            <div>
                                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>{t('roles.rolesTab')}</h2>
                                <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginTop: '4px' }}>Ekibinizin yetkilerini ve erişim seviyelerini yönetin.</p>
                            </div>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                {canManageRoles && roles.length < 5 && !loading && (
                                    <button 
                                        onClick={handleSeedDefaultRoles} 
                                        className="btn-secondary" 
                                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '12px', fontWeight: 600 }}
                                    >
                                        <RefreshCw size={18} /> {roles.length > 0 ? 'Eksik Rolleri Yükle' : 'Varsayılan Rolleri Yükle'}
                                    </button>
                                )}
                                {canManageRoles && (
                                    <button 
                                        onClick={() => { setEditingRole(null); setRoleData(initialRole); setModalOpen('role'); }} 
                                        className="btn-primary" 
                                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '12px', fontWeight: 700 }}
                                    >
                                        <ShieldCheck size={18} /> {t('roles.addRole')}
                                    </button>
                                )}
                            </div>
                        </div>
                        {loading ? <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100px' }}><RefreshCw className="animate-spin" /></div> : (
                            <RoleGrid roles={roles} canManageRoles={canManageRoles} onEdit={(r) => { setEditingRole(r); setRoleData(r); setModalOpen('role'); }} onDelete={handleDeleteRole} t={t} />
                        )}
                    </section>
                )}

                {/* 7. Entegrasyonlar Tab */}
                {activeTab === 'integrations' && (
                    <Integrations
                        tenantData={tenantData}
                        effectiveTenantId={effectiveTenantId}
                        handleUpdateField={handleUpdateField}
                    />
                )}

                {/* 8. Ödemeler ve Planlar Tab */}
                {activeTab === 'payments' && (
                    <SubscriptionPayments tenantData={tenantData} />
                )}
            </div>

            <RoleModal isOpen={modalOpen === 'role'} onClose={() => setModalOpen(null)} onSave={handleSaveRole} role={roleData} setRole={setRoleData} isEditing={!!editingRole} t={t} />
            <InviteUserModal isOpen={modalOpen === 'user'} onClose={() => setModalOpen(null)} onInvite={(u) => roleService.createInvite(effectiveTenantId, u, user.uid, userData.email)} roles={roles} t={t} />
        </div>
    );
};

export default Settings;